import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo } from "react";
import {
  GetRecurringTransactionsDocument,
  type GetRecurringTransactionsQuery,
  type GetRecurringTransactionsQueryVariables,
} from "@/src/gql/graphql";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import {
  EMPTY_INFINITE_RESULT,
  EMPTY_PAGINATED_RESULT,
  useStableMutationResult,
} from "@/lib/hooks/api/shared/stable-entity-hook-results";
import { mapRecurringTransactionEdges } from "@/lib/types/quickbooks/recurring-transactions";
import {
  useCreateRecurringTransaction,
  useDeleteRecurringTransaction,
  useUpdateRecurringTransaction,
} from "./recurring-transactions-mutations";
import { useRecurringTransaction } from "./recurring-transactions-queries";

/**
 * Shared by both recurring-transaction entity-hook objects. Each is allocated
 * ONCE at module scope — the object is a dependency of
 * `useBuildActionContext`'s `useMemo` and closes over nothing render-scoped.
 * See `lib/hooks/api/shared/contacts-graphql.ts`.
 */
const useRecurringTransactionSingle = (id: string) => {
  const result = useRecurringTransaction(id);
  return useMemo(
    () => ({
      data: result.data || undefined,
      isLoading: result.loading,
      error: result.error || null,
      refetch: result.refetch,
    }),
    [result.data, result.loading, result.error, result.refetch]
  );
};

const useRecurringTransactionCreate = () => {
  const { createTransaction, loading } = useCreateRecurringTransaction();
  const mutateAsync = useCallback(
    async (data: Partial<RecurringTransaction> & { id?: string }) => {
      const result = await createTransaction({
        customer_id: data.customer_id as string,
        item: data.item as string,
        description: data.description,
        amount: data.amount as number,
        quantity: data.quantity !== undefined ? Number(data.quantity) : undefined,
        quickbooks_realm_id: data.quickbooks_realm_id,
      });
      return result as RecurringTransaction;
    },
    [createTransaction]
  );
  return useStableMutationResult(mutateAsync, loading);
};

const useRecurringTransactionUpdate = () => {
  const { updateTransaction, loading } = useUpdateRecurringTransaction();
  const mutateAsync = useCallback(
    async (data: Partial<RecurringTransaction> & { id: string }) => {
      const result = await updateTransaction(data.id, data);
      return result as RecurringTransaction;
    },
    [updateTransaction]
  );
  return useStableMutationResult(mutateAsync, loading);
};

const useRecurringTransactionDelete = () => {
  const { deleteTransaction, loading } = useDeleteRecurringTransaction();
  const mutateAsync = useCallback(
    async (id: string) => {
      await deleteTransaction(id);
      return id;
    },
    [deleteTransaction]
  );
  return useStableMutationResult(mutateAsync, loading);
};

const RECURRING_TRANSACTION_ENTITY_HOOKS: EntityHooks<RecurringTransaction> = {
  usePaginated: () => EMPTY_PAGINATED_RESULT,
  useInfinite: () => EMPTY_INFINITE_RESULT,
  useSingle: useRecurringTransactionSingle,
  useCreate: useRecurringTransactionCreate,
  useUpdate: useRecurringTransactionUpdate,
  useDelete: useRecurringTransactionDelete,
};

export function useRecurringTransactionHooks(): EntityHooks<RecurringTransaction> {
  return RECURRING_TRANSACTION_ENTITY_HOOKS;
}

const RECURRING_TRANSACTION_PAGE_ENTITY_HOOKS: EntityHooks<RecurringTransaction> = {
  queryDocument: GetRecurringTransactionsDocument,
  usePaginated: (params?: Partial<GetRecurringTransactionsQueryVariables>) => {
    const result = useQuery<GetRecurringTransactionsQuery>(GetRecurringTransactionsDocument, {
      variables: params,
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      // Match the canonical list-query policy (see use-paginated-collection.ts):
      // cache-first lets a filtered list render a stale/empty *Collection until
      // a manual refetch. This hook keeps its bespoke edge-mapper + useMemo.
      fetchPolicy: "cache-and-network",
    });

    const transactions = useMemo(
      () => mapRecurringTransactionEdges(result.data?.recurring_transactionsCollection?.edges),
      [result.data]
    );

    return useMemo(
      () => ({
        data: result.data
          ? {
              data: transactions,
              totalCount: result.data.recurring_transactionsCollection?.totalCount || 0,
              pageInfo: result.data.recurring_transactionsCollection?.pageInfo,
            }
          : undefined,
        isLoading: result.loading,
        error: result.error || null,
        refetch: result.refetch,
      }),
      [result, transactions]
    );
  },

  useInfinite: () => EMPTY_INFINITE_RESULT,
  useSingle: useRecurringTransactionSingle,
  useCreate: useRecurringTransactionCreate,
  useUpdate: useRecurringTransactionUpdate,
  useDelete: useRecurringTransactionDelete,
};

export function useRecurringTransactionHooksForPage(): EntityHooks<RecurringTransaction> {
  return RECURRING_TRANSACTION_PAGE_ENTITY_HOOKS;
}
